import type { DataTableColumn } from 'mantine-datatable';
import { useCallback, useMemo, useState } from 'react';

import { useCurrencyQueryActions } from '@/api/hooks/currencyQueryActions';
import { useEstimationCostQueryActions } from '@/api/hooks/estimationCostQueryActions';
import type { CurrencyResponse, EstimationCostResponse } from '@/api/schema/data-contracts';
import { ApiEstimationItemPurpose } from '@/api/schema/data-contracts';
import { AssumptionsModal } from '@/components/Assumption';
import { Stack } from '@/components/atoms/Stack';
import TableSkeleton from '@/components/atoms/TableSkeleton';
import Search from '@/components/molecules/Search';
import { CommentsModal } from '@/components/organism/Comments/CommentsModal';
import { TableColumnsSelector } from '@/components/organism/DataTable/components/columnsSelector';
import { ExportButtonsSelector } from '@/components/organism/DataTable/components/exportSelector';
import { TablePhasesSelector } from '@/components/organism/DataTable/components/phasesSelector';
import { eExportFormat } from '@/components/organism/DataTable/model';
import type { EstimationExportButtonsProps } from '@/components/organism/EstimationExportButtons';
import { messages as sidebarMessageDefinitions } from '@/components/organism/Sidebar/messages';
import { useMessages } from '@/hooks/messages';
import { useEstimationExport } from '@/hooks/useEstimationExport';
import { Route } from '@/routes/estimation/detail/$estimationId/$tabId';
import type { OmitSafely } from '@/types';
import { getPhaseAndDescendantIds } from '@/utils/phasesFiltering';
import { sortCostTypes, sortResourceTypes } from '@/utils/sorting';

import type { CostEstimationItemsGroupedTableProps } from '../../CostEstimationItemsGroupedTables';
import EstimationItemDetailModal from '../../EstimationItemDetailModal';
import type { EstimationItemsGroupedTableProps } from '../../EstimationItemsGroupedTables';
import { Layout } from '../../Layout';
import {
  getCostEstimationItemDetail,
  getResourceEstimationItemDetail,
} from '../../mappers/estimationItemDetailMapper';
import { estimationTabMessageDefinitions } from '../../messages';
import type {
  CostEstimationItemsTableRecord,
  CostModel,
  EstimationItemActionCallback,
  ResourceEstimationItemsTableRecord,
} from '../../model';
import { useEstimationItemsTableRecords } from '../../useEstimationItemsTableRecords';
import DirectCostEstimationItemDetailModal from '../DirectCost/EstimationItemDetailModal';
import { directCostsMessageDefinitions } from '../DirectCost/messages';
import DirectCostSummaryTable from '../DirectCost/SummaryTable';
import { TopLevelEstimationItemsForm as DirectCostTopLevelEstimationItemsForm } from '../DirectCost/TopLevelEstimationItemsForm';
import { CostingTables } from '../ResourceCost/CostingTables';
import { resourceCostsMessageDefinitions } from '../ResourceCost/messages';
import ResourceCostSummaryTable from '../ResourceCost/SummaryTable';
import SummaryTable from './SummaryTable';

const costItemsEmptyArray: Array<CostEstimationItemsTableRecord> = [];
const resourceItemsEmptyArray: Array<ResourceEstimationItemsTableRecord> = [];

const resourceCostExportOutputOptions = {
  showResourcePurposeEstimationItems: true,
  showEstimateColumn: true,
  showResourceCostColumns: true,
  showCostSumColumn: true,
};

const directCostExportOutputOptions = {
  showCostPurposeEstimationItems: true,
  showDirectCostColumns: true,
  showCostSumColumn: true,
};

const EstimationDetailCostSummaryTab = () => {
  const { estimationId } = Route.useLoaderData();
  const resourceCostsMessages = useMessages(resourceCostsMessageDefinitions);
  const directCostsMessages = useMessages(directCostsMessageDefinitions);
  const sidebarMessages = useMessages(sidebarMessageDefinitions);
  const tabMessages = useMessages(estimationTabMessageDefinitions);
  const [searchText, setSearchText] = useState('');
  const [hiddenCostTypeColumns, setHiddenCostTypeColumns] = useState<string[]>([]);
  const [hiddenDirectCostColumns, setHiddenDirectCostColumns] = useState<string[]>([]);
  const [hiddenResourcePhases, setHiddenResourcePhases] = useState<string[]>([]);
  const [hiddenCostPhases, setHiddenCostPhases] = useState<string[]>([]);

  const {
    isLoading: isLoadingEstimationWithDependencies,
    data,
    getCostEstimationItemsTableRecordsByParent,
    getResourceEstimationItemsTableRecordsByParent,
  } = useEstimationItemsTableRecords(estimationId, {
    shouldFetchEstimationItemCosts: true,
    shouldFetchResources: true,
  });

  const { useGetCurrencies } = useCurrencyQueryActions();
  const { data: currencies, isLoading: isLoadingCurrencies } = useGetCurrencies() as {
    data: CurrencyResponse[] | undefined;
    isLoading: boolean;
  };

  const { useGetEstimationCosts } = useEstimationCostQueryActions();
  const { data: estimationCost, isLoading: isLoadingEstimationCost } = useGetEstimationCosts(
    estimationId,
  ) as { data: EstimationCostResponse | '' | undefined; isLoading: boolean };

  const [estimationItemIdForAssumptionsModal, setEstimationItemIdForAssumptionsModal] =
    useState<string>();
  const [estimationItemIdForCommentsModal, setEstimationItemIdForCommentsModal] =
    useState<string>();
  const [resourceEstimationItemIdsForDetailsModal, setResourceEstimationItemIdsForDetailsModal] =
    useState<string[]>([]);
  const [costEstimationItemIdsForDetailsModal, setCostEstimationItemIdsForDetailsModal] = useState<
    string[]
  >([]);

  const topLevelResourceEstimationItems = useMemo(
    () =>
      getResourceEstimationItemsTableRecordsByParent(undefined).filter((item) => !item.isOmitted),
    [getResourceEstimationItemsTableRecordsByParent],
  );

  const topLevelCostEstimationItems = useMemo(
    () => getCostEstimationItemsTableRecordsByParent(undefined).filter((item) => !item.isOmitted),
    [getCostEstimationItemsTableRecordsByParent],
  );

  // Filter out hidden phases and their descendants
  const filterByHiddenPhases = useCallback(
    <T extends { id: string }>(items: T[], hiddenPhases: string[]): T[] => {
      if (!data || hiddenPhases.length === 0) {
        return items;
      }

      const hiddenPhaseAndDescendantIds = new Set<string>();
      hiddenPhases.forEach((phaseId) => {
        const ids = getPhaseAndDescendantIds(phaseId, data.estimationItems);
        ids.forEach((id) => hiddenPhaseAndDescendantIds.add(id));
      });

      return items.filter((item) => !hiddenPhaseAndDescendantIds.has(item.id));
    },
    [data],
  );

  const filteredTopLevelResourceEstimationItems = useMemo(
    () => filterByHiddenPhases(topLevelResourceEstimationItems, hiddenResourcePhases),
    [filterByHiddenPhases, topLevelResourceEstimationItems, hiddenResourcePhases],
  );

  const filteredTopLevelCostEstimationItems = useMemo(
    () => filterByHiddenPhases(topLevelCostEstimationItems, hiddenCostPhases),
    [filterByHiddenPhases, topLevelCostEstimationItems, hiddenCostPhases],
  );

  // Get phases for the selectors (top-level items by purpose)
  const { resourcePhasesForSelector, costPhasesForSelector } = useMemo(() => {
    if (!data) {
      return { resourcePhasesForSelector: [], costPhasesForSelector: [] };
    }

    const topLevelItems = data.estimationItems.filter((item) => !item.parentId);

    return {
      resourcePhasesForSelector: topLevelItems
        .filter((item) => item.purpose === ApiEstimationItemPurpose.RESOURCE)
        .map((item) => ({ id: item.id, name: item.name })),
      costPhasesForSelector: topLevelItems
        .filter((item) => item.purpose === ApiEstimationItemPurpose.COST)
        .map((item) => ({ id: item.id, name: item.name })),
    };
  }, [data]);

  const calculatedResultByEstimationItemId = useMemo(
    () =>
      new Map<string, number>(
        data?.estimationItems.map((item) => [item.id, item.calculatedResult ?? 0]),
      ),
    [data?.estimationItems],
  );

  const costModel = useMemo<CostModel | undefined>(() => {
    if (estimationCost === undefined || data?.estimationModel.currencyIds === undefined) {
      return undefined;
    }

    const allResourceTypes = sortResourceTypes(
      data.estimationModel.resourceTypes,
      data.estimationModel.resourceTypeGroups,
    );

    if (estimationCost === '') {
      return {
        currencyId: data.estimationModel.currencyIds[0],
        resourceTypeCosts: allResourceTypes.map((resourceType) => ({
          resourceTypeId: resourceType.id,
          costTypeId: resourceType.costTypeId,
          price: resourceType.resourceCost ?? 0,
        })),
      };
    }

    return {
      currencyId: estimationCost.currencyId,
      resourceTypeCosts: allResourceTypes.map((resourceType) => {
        const storedCost = estimationCost.resourceTypeCosts.find(
          (resourceTypeCost) => resourceTypeCost.resourceTypeId === resourceType.id,
        );
        return {
          resourceTypeId: resourceType.id,
          costTypeId: resourceType.costTypeId,
          price:
            storedCost === undefined || storedCost.updatedAt === null
              ? (resourceType.resourceCost ?? 0)
              : storedCost.price,
        };
      }),
    };
  }, [estimationCost, data]);

  const combinedResourceAndCostItemsExportProps = useMemo<
    OmitSafely<EstimationExportButtonsProps, 'parentEstimationItemId'>
  >(
    () => ({
      estimationId,
      costModel,
      title: `${sidebarMessages.detailedEstimation()} - ${tabMessages.costSummaryLabel()}`,
      outputOptionsArray: [
        {
          ...resourceCostExportOutputOptions,
          tableTitle: resourceCostsMessages.costBreakDownTitle(),
        },
        {
          ...directCostExportOutputOptions,
          tableTitle: directCostsMessages.costBreakDownTitle(),
        },
      ],
      hiddenCostTypeColumns,
      hiddenPhases: [...hiddenResourcePhases, ...hiddenCostPhases],
    }),
    [
      estimationId,
      costModel,
      resourceCostsMessages,
      directCostsMessages,
      sidebarMessages,
      tabMessages,
      hiddenCostTypeColumns,
      hiddenResourcePhases,
      hiddenCostPhases,
    ],
  );

  const combinedExporter = useEstimationExport(combinedResourceAndCostItemsExportProps);
  const handleExportButtonClick = useCallback(
    (format: eExportFormat) => combinedExporter(format, undefined),
    [combinedExporter],
  );

  const exportConfiguration = useMemo<eExportFormat[]>(
    () => [
      eExportFormat.EXCEL,
      eExportFormat.PDF,
      eExportFormat.CSV,
      eExportFormat.JSON,
      eExportFormat.PRINT,
    ],
    [],
  );

  const costTypeColumns: DataTableColumn<{ id: string }>[] = useMemo(() => {
    if (!data?.estimationModel) {
      return [];
    }
    const allCostTypes = sortCostTypes(
      data.estimationModel.costTypes,
      data.estimationModel.costTypeGroups,
    );
    return allCostTypes.map((costType) => ({
      accessor: costType.id,
      title: costType.name,
    }));
  }, [data]);

  const handleColumnsChange = useCallback(
    (visibleColumns: DataTableColumn<{ id: string }>[]) => {
      const visibleAccessors = new Set(visibleColumns.map((col) => col.accessor as string));
      const allAccessors = costTypeColumns.map((col) => col.accessor as string);
      const hidden = allAccessors.filter((accessor) => !visibleAccessors.has(accessor));
      setHiddenCostTypeColumns(hidden);
    },
    [costTypeColumns],
  );

  const handleDirectCostColumnsChange = useCallback(
    (visibleColumns: DataTableColumn<{ id: string }>[]) => {
      const visibleAccessors = new Set(visibleColumns.map((col) => col.accessor as string));
      const allAccessors = costTypeColumns.map((col) => col.accessor as string);
      const hidden = allAccessors.filter((accessor) => !visibleAccessors.has(accessor));
      setHiddenDirectCostColumns(hidden);
    },
    [costTypeColumns],
  );

  const currencyCode = useMemo(() => {
    if (!currencies || !estimationCost) {
      return undefined;
    }
    return currencies.find(
      (currency: CurrencyResponse) => currency.id === estimationCost.currencyId,
    )?.code;
  }, [currencies, estimationCost]);

  if (isLoadingEstimationWithDependencies || isLoadingEstimationCost || isLoadingCurrencies) {
    return (
      <Layout>
        <h2 className='mt-5'>{resourceCostsMessages.costBreakDownTitle()}</h2>
        <TableSkeleton />
        <TableSkeleton />
      </Layout>
    );
  }

  if (data === undefined || currencies === undefined) {
    return (
      <Layout>
        <TableSkeleton />
      </Layout>
    );
  }

  if (!data.estimationItemCosts) {
    throw new Error('Estimation item costs cannot be undefined.');
  }

  if (!estimationCost) {
    return <Layout>Estimation cost not defined yet</Layout>; // TODO: we should lock this tab when estimationCost entity hasn't been created yet
  }

  const onAssumptionsClicked: EstimationItemActionCallback = setEstimationItemIdForAssumptionsModal;
  const onCommentsClicked: EstimationItemActionCallback = setEstimationItemIdForCommentsModal;
  const onResourceItemDetailsClicked: EstimationItemActionCallback = (estimationItemId: string) => {
    setResourceEstimationItemIdsForDetailsModal([
      ...resourceEstimationItemIdsForDetailsModal,
      estimationItemId,
    ]);
  };
  const onCostItemDetailsClicked: EstimationItemActionCallback = (estimationItemId: string) => {
    setCostEstimationItemIdsForDetailsModal([
      ...costEstimationItemIdsForDetailsModal,
      estimationItemId,
    ]);
  };

  const resourceTableProps: OmitSafely<
    EstimationItemsGroupedTableProps,
    'estimationItems' | 'control' | 'searchText' | 'formModelEstimationItems'
  > = {
    estimationModel: data.estimationModel,
    methodologies: data.methodologies,
    costs: costModel,
    showButtons: true,
    showMethodology: false,
    showResponsible: false,
    showParameters: false,
    showCosts: true,
    isReadOnly: true,
    onIncludeClicked: () => Promise.reject(new Error('Read-only')),
    estimationUnit: data.estimation.unit,
    onAssumptionsClicked,
    onCommentsClicked: onCommentsClicked,
    onDetailsClicked: onResourceItemDetailsClicked,
    calculatedResultByEstimationItemId,
    hiddenCostTypeColumns,
  };

  const costTableProps: OmitSafely<
    CostEstimationItemsGroupedTableProps,
    'estimationItems' | 'control' | 'onIncludeClicked' | 'searchText'
  > = {
    estimationModel: data.estimationModel,
    currencyCode: currencyCode,
    showButtons: true,
    isReadOnly: true,
    onAssumptionsClicked,
    onCommentsClicked,
    onDetailsClicked: onCostItemDetailsClicked,
    hiddenCostTypeColumns: hiddenDirectCostColumns,
  };

  return (
    <Layout>
      <Stack gap={10}>
        <div className='mt-4 flex justify-end gap-2 rounded-xl'>
          <Search handleOnChange={setSearchText} />
          <ExportButtonsSelector
            exportConfiguration={exportConfiguration}
            onExportButtonClick={handleExportButtonClick}
          />
          {resourcePhasesForSelector.length > 0 && (
            <TablePhasesSelector
              phases={resourcePhasesForSelector}
              hiddenPhases={hiddenResourcePhases}
              onPhasesChange={setHiddenResourcePhases}
            />
          )}
          {costTypeColumns.length > 0 && (
            <TableColumnsSelector
              columns={costTypeColumns}
              onColumnsChange={handleColumnsChange}
              hiddenColumns={hiddenCostTypeColumns}
            />
          )}
        </div>
        <SummaryTable
          resourceEstimationItems={filteredTopLevelResourceEstimationItems}
          costEstimationItems={filteredTopLevelCostEstimationItems}
          estimationModel={data.estimationModel}
          costs={costModel}
          hiddenCostTypeColumns={hiddenCostTypeColumns}
        />
        <h2 className='mt-4'>{resourceCostsMessages.costBreakDownTitle()}</h2>
        <CostingTables
          data={data}
          estimationCost={costModel}
          tableProps={{ ...resourceTableProps, searchText }}
          topLevelEstimationItems={filteredTopLevelResourceEstimationItems}
        />
        <ResourceCostSummaryTable
          estimationItems={filteredTopLevelResourceEstimationItems}
          estimationModel={data.estimationModel}
          costs={costModel}
          showCosts
          hiddenCostTypeColumns={hiddenCostTypeColumns}
        />
        <hr />
        <div className='mt-4 flex items-center justify-between gap-2 rounded-xl'>
          <h2>{directCostsMessages.costBreakDownTitle()}</h2>
          <div className='flex gap-2'>
            {costPhasesForSelector.length > 0 && (
              <TablePhasesSelector
                phases={costPhasesForSelector}
                hiddenPhases={hiddenCostPhases}
                onPhasesChange={setHiddenCostPhases}
              />
            )}
            {costTypeColumns.length > 0 && (
              <TableColumnsSelector
                columns={costTypeColumns}
                onColumnsChange={handleDirectCostColumnsChange}
                hiddenColumns={hiddenDirectCostColumns}
              />
            )}
          </div>
        </div>
        <DirectCostTopLevelEstimationItemsForm
          topLevelEstimationItems={filteredTopLevelCostEstimationItems}
          tableProps={{ ...costTableProps, searchText }}
          onSubmit={() => Promise.reject(new Error('Read-only'))}
          keepDirtyValues={false}
        />
        <DirectCostSummaryTable
          estimationItems={filteredTopLevelCostEstimationItems}
          estimationModel={data.estimationModel}
          costs={costModel}
          hiddenCostTypeColumns={hiddenDirectCostColumns}
        />
      </Stack>
      {resourceEstimationItemIdsForDetailsModal.map((resourceEstimationItemIdForDetail) => (
        <EstimationItemDetailModal
          key={resourceEstimationItemIdForDetail}
          open={!!resourceEstimationItemIdForDetail}
          onClose={() =>
            setResourceEstimationItemIdsForDetailsModal([
              ...resourceEstimationItemIdsForDetailsModal.slice(0, -1),
            ])
          }
          onSubmit={() => Promise.resolve()}
          shouldResetForm={true}
          onFormReset={() => {}}
          estimationItem={
            resourceEstimationItemIdForDetail === undefined
              ? undefined
              : getResourceEstimationItemDetail(resourceEstimationItemIdForDetail, data)
          }
          estimationItems={
            resourceEstimationItemIdForDetail
              ? getResourceEstimationItemsTableRecordsByParent(resourceEstimationItemIdForDetail)
              : resourceItemsEmptyArray
          }
          estimationMethodologyId={data.estimation.methodologyId}
          estimationCultureName={data.estimation.cultureName}
          exportProps={combinedResourceAndCostItemsExportProps}
          {...resourceTableProps}
        />
      ))}
      {costEstimationItemIdsForDetailsModal.map((costEstimationItemIdForDetail) => (
        <DirectCostEstimationItemDetailModal
          key={costEstimationItemIdForDetail}
          open={!!costEstimationItemIdForDetail}
          onClose={() =>
            setCostEstimationItemIdsForDetailsModal([
              ...costEstimationItemIdsForDetailsModal.slice(0, -1),
            ])
          }
          onSubmit={() => Promise.resolve()}
          estimationItem={
            costEstimationItemIdForDetail === undefined
              ? undefined
              : getCostEstimationItemDetail(costEstimationItemIdForDetail, data)
          }
          estimationItems={
            costEstimationItemIdForDetail
              ? getCostEstimationItemsTableRecordsByParent(costEstimationItemIdForDetail)
              : costItemsEmptyArray
          }
          exportProps={combinedResourceAndCostItemsExportProps}
          keepDirtyValues={false}
          {...costTableProps}
        />
      ))}
      <AssumptionsModal
        estimationId={estimationId}
        onClose={() => setEstimationItemIdForAssumptionsModal(undefined)}
        estimationItem={data.estimationItems.find(
          (x) => x.id === estimationItemIdForAssumptionsModal,
        )}
      />
      <CommentsModal
        commentType='ESTIMATION_ITEM'
        commentContext='COSTING'
        size='s'
        estimationId={data.estimation.id}
        estimationItem={data.estimationItems.find((x) => x.id === estimationItemIdForCommentsModal)}
        onClose={() => setEstimationItemIdForCommentsModal(undefined)}
      />
    </Layout>
  );
};

export default EstimationDetailCostSummaryTab;
